import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import {
  Box,
  Typography,
  TextField,
  Checkbox,
  FormControlLabel,
  Radio,
  RadioGroup,
  CircularProgress,
  InputAdornment,
  Paper,
} from "@mui/material";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DesktopDatePicker } from "@mui/x-date-pickers/DesktopDatePicker";
import dayjs from "dayjs";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import CommonSelect from "../../components/common/CommonSelect";
import { fetchAssessmentTemplateByType } from "../../api/assessment";

const buildFormPayload = (templateData, flatFormState) => {
  const payload = {};
  if (templateData?.sections) {
    Object.keys(templateData.sections).forEach((sectionKey) => {
      payload[sectionKey] = {};
      const fields = templateData.sections[sectionKey].fields;
      if (fields) {
        Object.keys(fields).forEach((fieldKey) => {
          const field = fields[fieldKey];
          if (field.type === "group") {
            payload[sectionKey][fieldKey] = {};
            if (field.fields) {
              Object.keys(field.fields).forEach((groupFieldKey) => {
                payload[sectionKey][fieldKey][groupFieldKey] =
                  flatFormState[`${fieldKey}.${groupFieldKey}`] ?? "";
              });
            }
          } else if (field.branches) {
            const branchValue = flatFormState[fieldKey];
            const updatedBranches = {};
            Object.keys(field.branches).forEach((branchKey) => {
              const subQ = field.branches[branchKey].subQuestion;
              if (subQ) {
                const answerKey = `${fieldKey}_branch_${branchKey}`;
                updatedBranches[branchKey] = {
                  subQuestion: {
                    ...subQ,
                    value: flatFormState[answerKey] ?? null,
                  },
                };
              }
            });
            payload[sectionKey][fieldKey] = {
              value: branchValue !== undefined ? branchValue : null,
              branches: updatedBranches,
            };
          } else {
            payload[sectionKey][fieldKey] = flatFormState[fieldKey] ?? "";
          }
        });
      }
    });
  }
  return payload;
};

const fetchTemplateOrNull = async (assessmentType) => {
  try {
    return await fetchAssessmentTemplateByType(assessmentType);
  } catch (err) {
    console.error("Error fetching general assessment template directly:", err);
    return null;
  }
};

const getProgramStrings = (program) =>
  [program.groupName, program.programId, program.programName, program.program]
    .filter(Boolean)
    .map(String);

const isSavedPresent = (value) => value !== undefined && value !== null;

const hasValueWrapper = (saved) =>
  typeof saved === "object" && saved !== null && "value" in saved;

const getSelectedLabels = (options) =>
  options.filter((opt) => opt.is_selected).map((opt) => opt.label);

const getSelectedLabel = (options) =>
  options.find((opt) => opt.is_selected)?.label || "";

const resolveGroupSubFieldValue = (subField, savedSubVal) => {
  if (isSavedPresent(savedSubVal)) {
    return hasValueWrapper(savedSubVal) ? savedSubVal.value : savedSubVal;
  }
  if (subField.type === "boolean" || subField.type === "checkbox") {
    return subField.is_checked === undefined
      ? (subField.value ?? "")
      : subField.is_checked;
  }
  if (subField.type === "multi_select") {
    return subField.options ? getSelectedLabels(subField.options) : [];
  }
  if (subField.type === "single_select") {
    return subField.options ? getSelectedLabel(subField.options) : "";
  }
  return subField.value ?? "";
};

const resolveMultiSelectValue = (field, savedVal) => {
  if (!isSavedPresent(savedVal)) return getSelectedLabels(field.options);
  if (hasValueWrapper(savedVal)) return savedVal.value || [];
  return Array.isArray(savedVal) ? savedVal : [savedVal];
};

const resolveBooleanValue = (field, savedVal) => {
  if (!isSavedPresent(savedVal)) return field.is_checked || false;
  return hasValueWrapper(savedVal) ? savedVal.value : savedVal;
};

const resolveSingleSelectValue = (field, savedVal) => {
  if (!isSavedPresent(savedVal)) return getSelectedLabel(field.options);
  return hasValueWrapper(savedVal) ? savedVal.value : savedVal;
};

const getTypedResolver = (type) => {
  switch (type) {
    case "multi_select":
      return resolveMultiSelectValue;
    case "boolean":
    case "checkbox":
      return resolveBooleanValue;
    case "single_select":
      return resolveSingleSelectValue;
    default:
      return null;
  }
};

const initBranchState = (initialState, fieldKey, field, savedVal) => {
  if (!field.branches) return;
  const savedBranches = savedVal?.branches || field.branches;
  Object.keys(field.branches).forEach((branchKey) => {
    const savedSubQ = savedBranches?.[branchKey]?.subQuestion;
    initialState[`${fieldKey}_branch_${branchKey}`] = savedSubQ?.value || "";
  });
};

const initFieldState = (
  initialState,
  sectionKey,
  fieldKey,
  field,
  generalFormPayload,
) => {
  const savedVal = generalFormPayload?.[sectionKey]?.[fieldKey];
  if (field.type === "group") {
    if (field.fields) {
      Object.keys(field.fields).forEach((groupFieldKey) => {
        initialState[`${fieldKey}.${groupFieldKey}`] =
          resolveGroupSubFieldValue(
            field.fields[groupFieldKey],
            savedVal?.[groupFieldKey],
          );
      });
    }
    return;
  }
  const resolver = getTypedResolver(field.type);
  if (!resolver) {
    initialState[fieldKey] = (savedVal ?? field.value) || "";
    return;
  }
  initialState[fieldKey] = resolver(field, savedVal);
  initBranchState(initialState, fieldKey, field, savedVal);
};

const buildInitialFormState = (data, generalFormPayload) => {
  const initialState = {};
  if (data?.sections) {
    Object.keys(data.sections).forEach((sectionKey) => {
      const fields = data.sections[sectionKey].fields;
      Object.keys(fields).forEach((fieldKey) => {
        initFieldState(
          initialState,
          sectionKey,
          fieldKey,
          fields[fieldKey],
          generalFormPayload,
        );
      });
    });
  }
  return initialState;
};

const GeneralAssessment = ({
  memberData,
  assessmentType = "initial",
  selectedProgram,
  generalAssessmentId,
  generalAssessmentSchema,
  generalFormPayload,
  onInitialize,
  onFormStateChange,
  primaryColor,
  secondaryColor,
  borderColor,
  bgColor,
  columnsCount = 2,
  readOnly = false,
  warnings = {},
  payloadKey = "GENERAL",
}) => {
  const [assessmentData, setAssessmentData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [formState, setFormState] = useState({});
  const [anchorEls, setAnchorEls] = useState({});
  const [focusedField, setFocusedField] = useState(null);
  const selectRefs = React.useRef({});

  const handleOpenSelect = (fieldKey, event) => {
    if (readOnly) return;
    setAnchorEls((prev) => ({ ...prev, [fieldKey]: event.currentTarget }));
  };

  const handleCloseSelect = (fieldKey) => {
    setAnchorEls((prev) => ({ ...prev, [fieldKey]: null }));
  };

  useEffect(() => {
    if (!loading && onFormStateChange && assessmentData) {
      const nestedPayload = buildFormPayload(assessmentData, formState);
      onFormStateChange(nestedPayload);
    }
  }, [formState, assessmentData, onFormStateChange, loading]);

  useEffect(() => {
    const loadAssessment = async () => {
      setLoading(true);

      let data = null;
      if (generalAssessmentId || generalAssessmentSchema) {
        console.log(
          "GeneralAssessment found existing session and schema, skipping API call.",
        );
        data = generalAssessmentSchema;
      } else if (readOnly) {
        console.log(
          "GeneralAssessment in read-only preview, skipping initialize API call.",
        );
        data = await fetchTemplateOrNull(assessmentType);
      } else {
        data = await fetchTemplateOrNull(assessmentType);
      }

      setAssessmentData(data);

      const initialState = buildInitialFormState(data, generalFormPayload);
      setFormState(initialState);
      setLoading(false);
    };

    void loadAssessment();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    memberData.memberId,
    assessmentType,
    selectedProgram,
    generalAssessmentId,
    generalAssessmentSchema,
  ]);

  const handleFieldChange = (fieldKey, value) => {
    setFormState((prev) => ({
      ...prev,
      [fieldKey]: value,
    }));
  };

  const isFieldVisible = (field) => {
    if (!field) return false;
    if (field.specificPrograms && field.specificPrograms.length > 0) {
      if (!selectedProgram) return false;
      const activeProgramStrings = (
        Array.isArray(selectedProgram) ? selectedProgram : [selectedProgram]
      ).flatMap(getProgramStrings);

      const matched = field.specificPrograms.some((p) => {
        const normalizedP = String(p)
          .toLowerCase()
          .trim()
          .replace(/['\s_-]+/g, "");
        return activeProgramStrings.some((act) => {
          const normalizedAct = String(act)
            .toLowerCase()
            .trim()
            .replace(/['\s_-]+/g, "");
          return (
            normalizedAct.includes(normalizedP) ||
            normalizedP.includes(normalizedAct)
          );
        });
      });

      if (!matched) return false;
    }

    return true;
  };

  const isVisible = (fieldKey, field) => {
    if (!isFieldVisible(field)) return false;
    if (!field.dependency) return true;

    const { key, value } = field.dependency;
    const actualValue = formState[key];

    if (Array.isArray(value)) {
      return value.includes(actualValue);
    }
    return actualValue === value;
  };

  const toRadioValue = (value) => {
    if (value === true) return "yes";
    if (value === false) return "no";
    return "";
  };

  const toSelectedValuesArray = (isMultiple, subFieldValue) => {
    if (isMultiple) return subFieldValue || [];
    if (subFieldValue) return [subFieldValue];
    return [];
  };

  const resolveSubFieldType = (subQType, isFocusedOrHasValue) => {
    if (subQType === "time") {
      return isFocusedOrHasValue ? "time" : "text";
    }
    if (subQType === "number") {
      return "number";
    }
    return "text";
  };

  const resolveFieldInputType = (fieldType, isFocusedOrHasValue) => {
    if (fieldType === "time") {
      return isFocusedOrHasValue ? "time" : "text";
    }
    if (fieldType === "textarea") {
      return "text";
    }
    return fieldType;
  };

  const getFieldPlaceholderText = (field) => {
    if (field.type === "textarea") {
      return "Enter notes...";
    }
    if ((field.label || "").length > 14) {
      return "Enter...";
    }
    return `Enter ${field.label || "value"}`;
  };

  const renderSubTextareaInput = ({
    subFieldKey,
    subQ,
    subFieldValue,
    isSubMissing,
    subEffectiveBorderColor,
  }) => {
    return (
      <TextField
        fullWidth
        size="small"
        multiline
        rows={4}
        value={subFieldValue || ""}
        onChange={(e) => handleFieldChange(subFieldKey, e.target.value)}
        placeholder={subQ.placeholder || "Enter notes..."}
        disabled={readOnly}
        sx={{
          "& .MuiOutlinedInput-root": {
            borderRadius: "8px",
            backgroundColor: "#fff",
            "& fieldset": { borderColor: subEffectiveBorderColor },
            "&:hover fieldset": {
              borderColor: isSubMissing ? "#E05353" : primaryColor,
            },
          },
        }}
      />
    );
  };

  const renderSubBooleanInput = ({
    subFieldKey,
    subQ,
    subFieldValue,
    isSubMissing,
    subEffectiveBorderColor,
  }) => {
    return (
      <RadioGroup
        row
        value={toRadioValue(subFieldValue)}
        onChange={(e) =>
          handleFieldChange(subFieldKey, e.target.value === "yes")
        }
      >
        <FormControlLabel
          value="yes"
          disabled={readOnly}
          control={
            <Radio
              size="small"
              sx={{
                p: 0.5,
                color: borderColor,
                "&.Mui-checked": { color: `${primaryColor} !important` },
              }}
            />
          }
          label={
            <Typography variant="body2" sx={{ color: "#10100F" }}>
              Yes
            </Typography>
          }
        />
        <FormControlLabel
          value="no"
          disabled={readOnly}
          control={
            <Radio
              size="small"
              sx={{
                p: 0.5,
                color: borderColor,
                "&.Mui-checked": { color: `${primaryColor} !important` },
              }}
            />
          }
          label={
            <Typography variant="body2" sx={{ color: "#10100F" }}>
              No
            </Typography>
          }
        />
      </RadioGroup>
    );
  };

  const renderSubCheckboxInput = ({
    subFieldKey,
    subQ,
    subFieldValue,
    isSubMissing,
    subEffectiveBorderColor,
  }) => {
    return (
      <FormControlLabel
        sx={{ ml: -0.5 }}
        control={
          <Checkbox
            size="small"
            checked={!!subFieldValue}
            onChange={(e) => handleFieldChange(subFieldKey, e.target.checked)}
            disabled={readOnly}
            sx={{
              p: 0.5,
              color:
                isSubMissing && !subFieldValue
                  ? "#E05353 !important"
                  : borderColor,
              "&.Mui-checked": { color: `${primaryColor} !important` },
              "& .MuiSvgIcon-root": {
                color:
                  isSubMissing && !subFieldValue
                    ? "#E05353 !important"
                    : undefined,
              },
            }}
          />
        }
        label={
          <Typography
            variant="body2"
            sx={{
              color: isSubMissing && !subFieldValue ? "#E05353" : "#10100F",
            }}
          >
            {subQ.label || "Yes"}
          </Typography>
        }
      />
    );
  };

  const renderSubDateInput = ({
    subFieldKey,
    subQ,
    subFieldValue,
    isSubMissing,
    subEffectiveBorderColor,
  }) => {
    return (
      <LocalizationProvider dateAdapter={AdapterDayjs}>
        <DesktopDatePicker
          value={subFieldValue ? dayjs(subFieldValue) : null}
          onChange={(newValue) => {
            if (newValue === null) {
              handleFieldChange(subFieldKey, null);
            } else if (newValue.isValid()) {
              handleFieldChange(subFieldKey, newValue.format("YYYY-MM-DD"));
            }
          }}
          disabled={readOnly}
          slotProps={{
            textField: {
              size: "small",
              fullWidth: true,
              sx: {
                "& .MuiOutlinedInput-root": {
                  borderRadius: "8px",
                  backgroundColor: "#fff",
                  "& fieldset": { borderColor },
                  "&:hover fieldset": { borderColor: primaryColor },
                },
              },
            },
          }}
        />
      </LocalizationProvider>
    );
  };

  const renderSubSelectInput = ({
    subFieldKey,
    subQ,
    subFieldValue,
    isSubMissing,
    subEffectiveBorderColor,
  }) => {
    const isMultiple = subQ.type === "multi_select";
    const selectedValues = toSelectedValuesArray(isMultiple, subFieldValue);
    const options = (subQ.options || []).map((opt) =>
      typeof opt === "string" ? opt : opt.label,
    );

    return (
      <Box>
        <Box
          ref={(el) => (selectRefs.current[subFieldKey] = el)}
          onClick={(e) => handleOpenSelect(subFieldKey, e)}
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            height: "40px",
            px: 1.5,
            borderRadius: "8px",
            border: `1px solid ${borderColor}`,
            backgroundColor: readOnly ? "#F3F4F6" : "#fff",
            cursor: readOnly ? "not-allowed" : "pointer",
            "&:hover": {
              borderColor: readOnly ? borderColor : primaryColor,
            },
          }}
        >
          <Typography
            variant="body2"
            sx={{
              color: selectedValues.length > 0 ? "#10100F" : "#9F9DB6",
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
            }}
          >
            {selectedValues.length > 0
              ? selectedValues.join(", ")
              : `Select ${subQ.label}...`}
          </Typography>
          <KeyboardArrowDownIcon sx={{ color: "#6B7280", fontSize: "20px" }} />
        </Box>
        <CommonSelect
          id={`${subFieldKey}-select`}
          open={Boolean(anchorEls[subFieldKey])}
          anchorEl={anchorEls[subFieldKey]}
          onClose={() => handleCloseSelect(subFieldKey)}
          noteTypes={options}
          selectedTypes={selectedValues}
          multiple={isMultiple}
          handleSelectAll={() => {
            const isAllSelected = selectedValues.length === options.length;
            handleFieldChange(subFieldKey, isAllSelected ? [] : options);
          }}
          handleTypeToggle={(type) => {
            if (isMultiple) {
              const newValue = selectedValues.includes(type)
                ? selectedValues.filter((v) => v !== type)
                : [...selectedValues, type];
              handleFieldChange(subFieldKey, newValue);
            } else {
              handleFieldChange(subFieldKey, type);
              handleCloseSelect(subFieldKey);
            }
          }}
          width={
            selectRefs.current[subFieldKey]?.offsetWidth
              ? `${selectRefs.current[subFieldKey].offsetWidth - 50}px`
              : 200
          }
        />
      </Box>
    );
  };

  const renderSubTextInput = ({
    subFieldKey,
    subQ,
    subFieldValue,
    isSubMissing,
    subEffectiveBorderColor,
  }) => {
    return (
      <TextField
        fullWidth
        size="small"
        inputProps={{ maxLength: 255 }}
        InputProps={{
          endAdornment: (
            <InputAdornment position="end">
              <Typography
                sx={{
                  fontSize: "11px",
                  color: "#9F9DB6",
                  userSelect: "none",
                }}
              >
                {subFieldValue ? `${subFieldValue.length}/255` : "0/255"}
              </Typography>
            </InputAdornment>
          ),
        }}
        value={subFieldValue || ""}
        onChange={(e) => handleFieldChange(subFieldKey, e.target.value)}
        placeholder={subQ.placeholder || `Enter ${subQ.label || "value"}`}
        disabled={readOnly}
        sx={{
          "& .MuiOutlinedInput-root": {
            borderRadius: "8px",
            backgroundColor: "#fff",
            pr: 1,
            "& fieldset": { borderColor },
            "&:hover fieldset": { borderColor: primaryColor },
          },
          "& .MuiInputBase-input::placeholder": {
            opacity: "1 !important",
            color: "#9CA3AF",
          },
        }}
      />
    );
  };

  const renderSubDefaultInput = ({
    subFieldKey,
    subQ,
    subFieldValue,
    isSubMissing,
    subEffectiveBorderColor,
  }) => {
    return (
      <TextField
        fullWidth
        size="small"
        type={resolveSubFieldType(
          subQ.type,
          focusedField === subFieldKey || subFieldValue,
        )}
        value={subFieldValue || ""}
        onChange={(e) => handleFieldChange(subFieldKey, e.target.value)}
        placeholder={
          subQ.placeholder ||
          (subQ.type === "time"
            ? "HH:MM AM/PM"
            : `Enter ${subQ.label || "value"}`)
        }
        onFocus={() => setFocusedField(subFieldKey)}
        onBlur={() => setFocusedField(null)}
        disabled={readOnly}
        sx={{
          "& .MuiOutlinedInput-root": {
            borderRadius: "8px",
            backgroundColor: "#fff",
            "& fieldset": { borderColor },
            "&:hover fieldset": { borderColor: primaryColor },
          },
          "& .MuiInputBase-input::placeholder": {
            opacity: "1 !important",
            color: "#9CA3AF",
          },
        }}
      />
    );
  };

  const renderSubFieldInput = (subFieldKey, subQ, subFieldValue) => {
    const isSubMissing =
      subQ.required &&
      (subFieldValue === undefined ||
        subFieldValue === null ||
        subFieldValue === "" ||
        subFieldValue === false ||
        (Array.isArray(subFieldValue) && subFieldValue.length === 0));
    const subEffectiveBorderColor = isSubMissing ? "#E05353" : borderColor;

    switch (subQ.type) {
      case "textarea":
        return renderSubTextareaInput({
          subFieldKey,
          subQ,
          subFieldValue,
          isSubMissing,
          subEffectiveBorderColor,
        });
      case "boolean":
        return renderSubBooleanInput({
          subFieldKey,
          subQ,
          subFieldValue,
          isSubMissing,
          subEffectiveBorderColor,
        });
      case "checkbox":
        return renderSubCheckboxInput({
          subFieldKey,
          subQ,
          subFieldValue,
          isSubMissing,
          subEffectiveBorderColor,
        });
      case "date":
        return renderSubDateInput({
          subFieldKey,
          subQ,
          subFieldValue,
          isSubMissing,
          subEffectiveBorderColor,
        });
      case "single_select":
      case "multi_select":
        return renderSubSelectInput({
          subFieldKey,
          subQ,
          subFieldValue,
          isSubMissing,
          subEffectiveBorderColor,
        });
      case "text":
        return renderSubTextInput({
          subFieldKey,
          subQ,
          subFieldValue,
          isSubMissing,
          subEffectiveBorderColor,
        });
      case "time":
      case "number":
      default:
        return renderSubDefaultInput({
          subFieldKey,
          subQ,
          subFieldValue,
          isSubMissing,
          subEffectiveBorderColor,
        });
    }
  };

  const renderBranchingQuestions = (fieldKey, field) => {
    if (!field.branches) return null;

    const value = formState[fieldKey];
    const activeBranches = [];

    if (field.type === "boolean") {
      if (value === true && field.branches["true"]) {
        activeBranches.push({ key: "true", config: field.branches["true"] });
      } else if (value === false && field.branches["false"]) {
        activeBranches.push({ key: "false", config: field.branches["false"] });
      }
    } else if (field.type === "single_select") {
      if (value && field.branches[value]) {
        activeBranches.push({ key: value, config: field.branches[value] });
      }
    } else if (field.type === "multi_select") {
      const selected = Array.isArray(value) ? value : [];
      selected.forEach((opt) => {
        if (field.branches[opt]) {
          activeBranches.push({ key: opt, config: field.branches[opt] });
        }
      });
    }

    if (activeBranches.length === 0) return null;

    return (
      <>
        {activeBranches.map(({ key: branchKey, config }) => {
          const subQ = config.subQuestion;
          if (!subQ || !isFieldVisible(subQ)) return null;
          const subFieldKey = `${fieldKey}_branch_${branchKey}`;
          const subFieldValue = formState[subFieldKey];

          let subWidth = "100%";
          if (subQ.type !== "textarea" && subQ.type !== "group") {
            if (columnsCount === 2) {
              subWidth = "calc(50% - 12px)";
            } else if (columnsCount === 3) {
              subWidth = "calc(33.333% - 16px)";
            } else if (columnsCount === 4) {
              subWidth = "calc(25% - 18px)";
            }
          }

          return (
            <Box
              key={subFieldKey}
              sx={{
                width: subWidth,
                display: "flex",
                flexDirection: "column",
                mb: 2,
              }}
            >
              <Typography
                sx={{
                  fontSize: "13px",
                  color: "#403C6F",
                  fontWeight: 500,
                  mb: 1,
                  display: "block",
                  letterSpacing: "0.5px",
                }}
              >
                {subQ.label}{" "}
                {subQ.required && <span style={{ color: "#E05353" }}>*</span>}
              </Typography>
              <Box sx={{ mt: "auto" }}>
                {renderSubFieldInput(subFieldKey, subQ, subFieldValue)}
              </Box>
            </Box>
          );
        })}
      </>
    );
  };

  const resolveHoverBorderColor = (
    readOnly,
    isMissingRequired,
    effectiveBorderColor,
    primaryColor,
  ) => {
    if (readOnly) return effectiveBorderColor;
    if (isMissingRequired) return "#E05353";
    return primaryColor;
  };

  const getSelectDisplayText = (selectedValues, fieldLabel) => {
    if (selectedValues.length > 0) {
      return selectedValues.join(", ");
    }
    if (fieldLabel.length > 14) {
      return "Select...";
    }
    return `Select ${fieldLabel}...`;
  };
  const isValueEmpty = (value) =>
    value === undefined ||
    value === null ||
    value === "" ||
    (Array.isArray(value) && value.length === 0);

  const getIsGroupMissing = (
    hasSubFields,
    fieldRequired,
    hasMissingRequiredSubField,
    hasAnyEmptySubField,
    value,
  ) => {
    if (hasSubFields) {
      if (fieldRequired) {
        return hasMissingRequiredSubField || hasAnyEmptySubField;
      }
      return hasMissingRequiredSubField;
    }
    return fieldRequired && isValueEmpty(value);
  };

  const renderTextField = ({
    fieldKey,
    field,
    value,
    isMissingRequired,
    effectiveBorderColor,
  }) => {
    return (
      <Box
        sx={{
          mb: 2,
          display: "flex",
          flexDirection: "column",
          flexGrow: 1,
        }}
      >
        <Typography
          sx={{
            fontSize: "13px",
            color: "#403C6F",
            fontWeight: 500,
            mb: 1,
            display: "block",
            letterSpacing: "0.5px",
          }}
        >
          {field.label}{" "}
          {field.required && <span style={{ color: "#E05353" }}>*</span>}
        </Typography>
        <Box sx={{ mt: "auto" }}>
          <TextField
            fullWidth
            size="small"
            inputProps={{ maxLength: 255 }}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <Typography
                    sx={{
                      fontSize: "11px",
                      color: "#9F9DB6",
                      userSelect: "none",
                    }}
                  >
                    {value ? `${value.length}/255` : "0/255"}
                  </Typography>
                </InputAdornment>
              ),
            }}
            value={value || ""}
            onChange={(e) => handleFieldChange(fieldKey, e.target.value)}
            placeholder={
              field.placeholder ||
              ((field.label || "").length > 14
                ? "Enter..."
                : `Enter ${field.label || "value"}`)
            }
            disabled={readOnly}
            sx={{
              "& .MuiOutlinedInput-root": {
                borderRadius: "8px",
                backgroundColor: "#fff",
                pr: 1,
                "& fieldset": {
                  borderColor: `${effectiveBorderColor} !important`,
                },
                "&:hover fieldset": {
                  borderColor: `${isMissingRequired ? "#E05353" : primaryColor} !important`,
                },
              },
              "& .MuiInputBase-input::placeholder": {
                opacity: "1 !important",
                color: "#9CA3AF",
              },
            }}
          />
        </Box>
      </Box>
    );
  };

  const renderTextLikeField = ({
    fieldKey,
    field,
    value,
    isMissingRequired,
    effectiveBorderColor,
  }) => {
    return (
      <Box
        sx={{
          mb: 2,
          display: "flex",
          flexDirection: "column",
          flexGrow: 1,
        }}
      >
        <Typography
          sx={{
            fontSize: "13px",
            color: "#403C6F",
            fontWeight: 500,
            mb: 1,
            display: "block",
            letterSpacing: "0.5px",
          }}
        >
          {field.label}{" "}
          {field.required && <span style={{ color: "#E05353" }}>*</span>}
        </Typography>
        <Box sx={{ mt: "auto" }}>
          <TextField
            fullWidth
            size="small"
            type={resolveFieldInputType(
              field.type,
              focusedField === fieldKey || value,
            )}
            multiline={field.type === "textarea"}
            rows={field.type === "textarea" ? 6 : 1}
            value={value || ""}
            onChange={(e) => handleFieldChange(fieldKey, e.target.value)}
            onFocus={() => setFocusedField(fieldKey)}
            onBlur={() => setFocusedField(null)}
            placeholder={
              field.placeholder ||
              (field.type === "time"
                ? "HH:MM AM/PM"
                : getFieldPlaceholderText(field))
            }
            disabled={readOnly}
            sx={{
              "& .MuiOutlinedInput-root": {
                borderRadius: "8px",
                backgroundColor: "#fff",
                "& fieldset": {
                  borderColor: `${effectiveBorderColor} !important`,
                },
                "&:hover fieldset": {
                  borderColor: `${isMissingRequired ? "#E05353" : primaryColor} !important`,
                },
              },
              "& .MuiInputBase-input::placeholder": {
                opacity: "1 !important",
                color: "#9CA3AF",
              },
            }}
          />
        </Box>
      </Box>
    );
  };

  const renderDateField = ({
    fieldKey,
    field,
    value,
    isMissingRequired,
    effectiveBorderColor,
  }) => {
    return (
      <Box
        sx={{
          mb: 2,
          display: "flex",
          flexDirection: "column",
          flexGrow: 1,
        }}
      >
        <Typography
          sx={{
            fontSize: "13px",
            color: "#403C6F",
            fontWeight: 500,
            mb: 1,
            display: "block",
            letterSpacing: "0.5px",
          }}
        >
          {field.label}{" "}
          {field.required && <span style={{ color: "#E05353" }}>*</span>}
        </Typography>
        <Box sx={{ mt: "auto" }}>
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <DesktopDatePicker
              value={value ? dayjs(value) : null}
              onChange={(newValue) => {
                if (newValue === null) {
                  handleFieldChange(fieldKey, null);
                } else if (newValue.isValid()) {
                  handleFieldChange(fieldKey, newValue.format("YYYY-MM-DD"));
                }
              }}
              disabled={readOnly}
              slotProps={{
                textField: {
                  size: "small",
                  fullWidth: true,
                  error: isMissingRequired,
                  sx: {
                    "& .MuiOutlinedInput-root": {
                      borderRadius: "8px",
                      backgroundColor: "#fff",
                      "& fieldset": {
                        borderColor: `${effectiveBorderColor} !important`,
                      },
                      "& .MuiOutlinedInput-notchedOutline": {
                        borderColor: `${effectiveBorderColor} !important`,
                      },
                      "&:hover fieldset": {
                        borderColor: `${isMissingRequired ? "#E05353" : primaryColor} !important`,
                      },
                      "&:hover .MuiOutlinedInput-notchedOutline": {
                        borderColor: `${isMissingRequired ? "#E05353" : primaryColor} !important`,
                      },
                    },
                  },
                },
              }}
            />
          </LocalizationProvider>
        </Box>
      </Box>
    );
  };

  const renderSelectField = ({
    fieldKey,
    field,
    value,
    isMissingRequired,
    effectiveBorderColor,
  }) => {
    const isMultiple = field.type === "multi_select";
    const selectedValues = toSelectedValuesArray(isMultiple, value);
    const options = (field.options || []).map((opt) => opt.label);

    return (
      <Box
        sx={{
          mb: 2,
          display: "flex",
          flexDirection: "column",
          flexGrow: 1,
        }}
      >
        <Typography
          sx={{
            fontSize: "13px",
            color: "#403C6F",
            fontWeight: 500,
            mb: 1,
            display: "block",
            letterSpacing: "0.5px",
          }}
        >
          {field.label}{" "}
          {field.required && <span style={{ color: "#E05353" }}>*</span>}
        </Typography>
        <Box sx={{ mt: "auto" }}>
          <Box
            ref={(el) => (selectRefs.current[fieldKey] = el)}
            onClick={(e) => handleOpenSelect(fieldKey, e)}
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              height: "40px",
              px: 1.5,
              borderRadius: "8px",
              border: `1px solid ${effectiveBorderColor}`,
              backgroundColor: readOnly ? "#F3F4F6" : "#fff",
              cursor: readOnly ? "not-allowed" : "pointer",
              "&:hover": {
                borderColor: resolveHoverBorderColor(
                  readOnly,
                  isMissingRequired,
                  effectiveBorderColor,
                  primaryColor,
                ),
              },
            }}
          >
            <Typography
              variant="body2"
              sx={{
                color: selectedValues.length > 0 ? "#10100F" : "#9F9DB6",
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
              }}
            >
              {getSelectDisplayText(selectedValues, field.label)}
            </Typography>
            <KeyboardArrowDownIcon
              sx={{ color: "#6B7280", fontSize: "20px" }}
            />
          </Box>
          <CommonSelect
            id={`${fieldKey}-select`}
            open={Boolean(anchorEls[fieldKey])}
            anchorEl={anchorEls[fieldKey]}
            onClose={() => handleCloseSelect(fieldKey)}
            noteTypes={options}
            selectedTypes={selectedValues}
            multiple={isMultiple}
            handleSelectAll={() => {
              const isAllSelected = selectedValues.length === options.length;
              handleFieldChange(fieldKey, isAllSelected ? [] : options);
            }}
            handleTypeToggle={(type) => {
              if (isMultiple) {
                const newValue = selectedValues.includes(type)
                  ? selectedValues.filter((v) => v !== type)
                  : [...selectedValues, type];
                handleFieldChange(fieldKey, newValue);
              } else {
                handleFieldChange(fieldKey, type);
                handleCloseSelect(fieldKey);
              }
            }}
            width={
              selectRefs.current[fieldKey]?.offsetWidth
                ? `${selectRefs.current[fieldKey].offsetWidth - 60}px`
                : 200
            }
          />
        </Box>
      </Box>
    );
  };

  const renderBooleanField = ({
    fieldKey,
    field,
    value,
    isMissingRequired,
    effectiveBorderColor,
  }) => {
    return (
      <Box
        sx={{
          mb: 2,
          display: "flex",
          flexDirection: "column",
          flexGrow: 1,
        }}
      >
        <Typography
          sx={{
            fontSize: "13px",
            color: "#403C6F",
            fontWeight: 500,
            mb: 1,
            display: "block",
            letterSpacing: "0.5px",
          }}
        >
          {field.label}{" "}
          {field.required && <span style={{ color: "#E05353" }}>*</span>}
        </Typography>
        <Box sx={{ mt: "auto" }}>
          <RadioGroup
            row
            value={toRadioValue(value)}
            onChange={(e) =>
              handleFieldChange(fieldKey, e.target.value === "yes")
            }
          >
            <FormControlLabel
              value="yes"
              disabled={readOnly}
              control={
                <Radio
                  size="small"
                  sx={{
                    p: 0.5,
                    color: borderColor,
                    "&.Mui-checked": { color: "#403C6F !important" },
                  }}
                />
              }
              label={
                <Typography variant="body2" sx={{ color: "#10100F" }}>
                  Yes
                </Typography>
              }
            />
            <FormControlLabel
              value="no"
              disabled={readOnly}
              control={
                <Radio
                  size="small"
                  sx={{
                    p: 0.5,
                    color: borderColor,
                    "&.Mui-checked": { color: "#403C6F !important" },
                  }}
                />
              }
              label={
                <Typography variant="body2" sx={{ color: "#10100F" }}>
                  No
                </Typography>
              }
            />
          </RadioGroup>
        </Box>
      </Box>
    );
  };

  const renderCheckboxField = ({
    fieldKey,
    field,
    value,
    isMissingRequired,
    effectiveBorderColor,
  }) => {
    return (
      <Box
        sx={{
          mb: 2,
          display: "flex",
          flexDirection: "column",
          flexGrow: 1,
        }}
      >
        <Typography
          sx={{
            fontSize: "13px",
            color: "#403C6F",
            fontWeight: 500,
            mb: 1,
            display: "block",
            letterSpacing: "0.5px",
          }}
        >
          {field.label}{" "}
          {field.required && <span style={{ color: "#E05353" }}>*</span>}
        </Typography>
        <Box sx={{ mt: "auto" }}>
          <FormControlLabel
            sx={{ ml: -0.5 }}
            control={
              <Checkbox
                size="small"
                checked={!!value}
                onChange={(e) => handleFieldChange(fieldKey, e.target.checked)}
                disabled={readOnly}
                sx={{
                  p: 0.5,
                  color:
                    isMissingRequired && !value
                      ? "#E05353 !important"
                      : borderColor,
                  "&.Mui-checked": { color: "#403C6F !important" },
                  "& .MuiSvgIcon-root": {
                    color:
                      isMissingRequired && !value
                        ? "#E05353 !important"
                        : undefined,
                  },
                }}
              />
            }
            label={
              <Typography
                variant="body2"
                sx={{
                  color: isMissingRequired && !value ? "#E05353" : "#10100F",
                }}
              >
                {field.checkboxLabel || "Yes / Completed"}
              </Typography>
            }
          />
        </Box>
      </Box>
    );
  };

  const renderGroupField = ({
    fieldKey,
    field,
    value,
    isMissingRequired,
    effectiveBorderColor,
  }) => {
    const hasSubFields =
      field.fields &&
      typeof field.fields === "object" &&
      Object.keys(field.fields).length > 0;
    const subKeys = hasSubFields ? Object.keys(field.fields) : [];

    const hasMissingRequiredSubField = subKeys.some((subKey) => {
      const subField = field.fields[subKey];
      if (!subField?.required) return false;
      const subVal = formState[`${fieldKey}.${subKey}`];
      return (
        subVal === undefined ||
        subVal === null ||
        subVal === "" ||
        (Array.isArray(subVal) && subVal.length === 0)
      );
    });

    const hasAnyEmptySubField = subKeys.some((subKey) => {
      const subVal = formState[`${fieldKey}.${subKey}`];
      return (
        subVal === undefined ||
        subVal === null ||
        subVal === "" ||
        (Array.isArray(subVal) && subVal.length === 0)
      );
    });

    const isGroupMissing = getIsGroupMissing(
      hasSubFields,
      field.required,
      hasMissingRequiredSubField,
      hasAnyEmptySubField,
      value,
    );

    const groupBorderColor = isGroupMissing ? "#E05353" : borderColor;

    return (
      <Box
        sx={{
          mb: 2,
          p: 2,
          border: `1px solid ${groupBorderColor} !important`,
          borderRadius: "8px",
        }}
      >
        <Typography
          sx={{
            fontSize: "13px",
            color: "#403C6F",
            fontWeight: 500,
            mb: 2,
            display: "block",
            letterSpacing: "0.5px",
          }}
        >
          {field.label}{" "}
          {field.required && <span style={{ color: "#E05353" }}>*</span>}
        </Typography>
        <Box
          sx={{
            display: "flex",
            flexWrap: "wrap",
            gap: 2,
          }}
        >
          {Object.keys(field.fields).map((groupFieldKey) => (
            <Box
              key={groupFieldKey}
              sx={{
                width: {
                  xs: "100%",
                  sm: "calc(50% - 8px)",
                  md: "calc(25% - 12px)",
                },
              }}
            >
              {renderField(
                `${fieldKey}.${groupFieldKey}`,
                field.fields[groupFieldKey],
              )}
            </Box>
          ))}
        </Box>
      </Box>
    );
  };

  const renderField = (fieldKey, field) => {
    const value = formState[fieldKey];
    const isMissingRequired =
      field.required &&
      (value === undefined ||
        value === null ||
        value === "" ||
        value === false ||
        (Array.isArray(value) && value.length === 0));
    const effectiveBorderColor = isMissingRequired ? "#E05353" : borderColor;

    switch (field.type) {
      case "text":
        return renderTextField({
          fieldKey,
          field,
          value,
          isMissingRequired,
          effectiveBorderColor,
        });
      case "time":
      case "number":
      case "textarea":
        return renderTextLikeField({
          fieldKey,
          field,
          value,
          isMissingRequired,
          effectiveBorderColor,
        });
      case "date":
        return renderDateField({
          fieldKey,
          field,
          value,
          isMissingRequired,
          effectiveBorderColor,
        });
      case "single_select":
      case "multi_select":
        return renderSelectField({
          fieldKey,
          field,
          value,
          isMissingRequired,
          effectiveBorderColor,
        });
      case "boolean":
        return renderBooleanField({
          fieldKey,
          field,
          value,
          isMissingRequired,
          effectiveBorderColor,
        });
      case "checkbox":
        return renderCheckboxField({
          fieldKey,
          field,
          value,
          isMissingRequired,
          effectiveBorderColor,
        });
      case "group":
        return renderGroupField({
          fieldKey,
          field,
          value,
          isMissingRequired,
          effectiveBorderColor,
        });
      default:
        return null;
    }
  };

  if (loading) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: "300px",
        }}
      >
        <CircularProgress sx={{ color: primaryColor }} />
      </Box>
    );
  }

  const hasSchema =
    assessmentData?.sections && Object.keys(assessmentData.sections).length > 0;

  const resolveWarningMessage = () => {
    const pKey = String(payloadKey || "").toUpperCase();
    if (pKey.includes("CARE_PLAN") || pKey.includes("CAREPLAN")) {
      return (
        warnings?.CARE_PLAN_COORDINATION_COMPLETION ||
        warnings?.CARE_PLAN ||
        warnings?.care_plan ||
        warnings?.CAREPLAN ||
        warnings?.careplan
      );
    }
    if (pKey.includes("LIFESTYLE")) {
      return (
        warnings?.LIFESTYLE_BARRIERS_FUNCTIONAL_ASSESSMENT ||
        warnings?.LIFESTYLE ||
        warnings?.lifestyle
      );
    }
    if (pKey.includes("GENERAL") || pKey.includes("OTHER")) {
      return (
        warnings?.OTHER_GENERAL ||
        warnings?.GENERAL ||
        warnings?.general ||
        warnings?.OTHER ||
        warnings?.other
      );
    }
    return (
      warnings?.[payloadKey] ||
      warnings?.CARE_PLAN_COORDINATION_COMPLETION ||
      warnings?.LIFESTYLE_BARRIERS_FUNCTIONAL_ASSESSMENT ||
      warnings?.OTHER_GENERAL ||
      warnings?.GENERAL ||
      warnings?.OTHER
    );
  };

  const warningMessage = resolveWarningMessage();

  if (warningMessage || !hasSchema) {
    return (
      <Box sx={{}}>
        <Paper
          variant="outlined"
          sx={{
            p: 4,
            borderRadius: "12px",
            border: `1px solid ${borderColor}`,
            textAlign: "center",
            backgroundColor: "#F7F8FB",
          }}
        >
          <Typography variant="body1" sx={{ color: secondaryColor }}>
            {warningMessage ||
              "No assessment template or questions have been configured for the selected program and assessment type. Please contact your administrator."}
          </Typography>
        </Paper>
      </Box>
    );
  }

  return (
    <Box
      component="fieldset"
      disabled={readOnly}
      style={{ border: "none", padding: 0, margin: 0, minWidth: 0 }}
    >
      {Object.keys(assessmentData.sections).map((sectionKey) => {
        const section = assessmentData.sections[sectionKey];
        return (
          <Box key={sectionKey} sx={{ mb: 5 }}>
            <Box
              sx={{
                backgroundColor: "#ECECF1",
                p: 1.5,
                mb: 3,
                borderRadius: "4px",
              }}
            >
              <Typography
                variant="h6"
                fontWeight={700}
                sx={{ color: primaryColor, fontSize: "16px" }}
              >
                {section.label}
              </Typography>
            </Box>

            <Box
              sx={{
                display: "flex",
                flexWrap: "wrap",
                gap: 3,
                alignItems: "stretch",
              }}
            >
              {Object.keys(section.fields)
                .sort((a, b) => {
                  if (sectionKey === "narrative_attestation") {
                    const order = {
                      clinician_signature: 1,
                      signature_date: 2,
                      narrative_note: 3,
                    };
                    return (order[a] || 99) - (order[b] || 99);
                  }
                  return 0;
                })
                .map((fieldKey) => {
                  const field = section.fields[fieldKey];
                  if (!field?.type || !isVisible(fieldKey, field)) return null;

                  const supportedTypes = [
                    "text",
                    "date",
                    "time",
                    "number",
                    "textarea",
                    "single_select",
                    "multi_select",
                    "boolean",
                    "checkbox",
                    "group",
                  ];
                  if (!supportedTypes.includes(field.type)) return null;

                  let width = "100%";
                  if (
                    field.type !== "textarea" &&
                    field.type !== "group" &&
                    fieldKey !== "labs" &&
                    !(
                      sectionKey === "narrative_attestation" &&
                      fieldKey === "narrative_note"
                    )
                  ) {
                    if (columnsCount === 2) {
                      width = "calc(50% - 12px)";
                    } else if (columnsCount === 3) {
                      width = "calc(33.333% - 16px)";
                    } else if (columnsCount === 4) {
                      width = "calc(25% - 18px)";
                    }
                  }

                  return (
                    <React.Fragment key={fieldKey}>
                      <Box
                        sx={{ width, display: "flex", flexDirection: "column" }}
                      >
                        {renderField(fieldKey, field)}
                      </Box>
                      {renderBranchingQuestions(fieldKey, field)}
                    </React.Fragment>
                  );
                })}
            </Box>
          </Box>
        );
      })}
    </Box>
  );
};

GeneralAssessment.propTypes = {
  memberData: PropTypes.object.isRequired,
  assessmentType: PropTypes.string,
  selectedProgram: PropTypes.object,
  generalAssessmentId: PropTypes.number,
  generalAssessmentSchema: PropTypes.object,
  generalFormPayload: PropTypes.object,
  onInitialize: PropTypes.func,
  onFormStateChange: PropTypes.func,
  primaryColor: PropTypes.string.isRequired,
  secondaryColor: PropTypes.string.isRequired,
  borderColor: PropTypes.string.isRequired,
  bgColor: PropTypes.string.isRequired,
  columnsCount: PropTypes.number,
  readOnly: PropTypes.bool,
  warnings: PropTypes.object,
  payloadKey: PropTypes.string,
};

export default GeneralAssessment;
